import { WSMutationConfig } from "@ws-react-query";
import { useWSMutation } from "../../helpers";
import { WSServiceError } from "../../../utils/serviceHelper";

import { cleanCSVData } from "../../../shared/utils/csv";
import isEmpty from "lodash/isEmpty";
import { toWSDateString } from "@wingspanhq/fe-component-library";
import { downloadCSV } from "../../../modules/Reports/utils/sheetjs/downloadCSV";
import { useXWingspanExpansion } from "../../../shared/hooks/useXWingspanExpansion";
import { queriesCustomFields, queriesPayeeRows } from "@query";
import { convertDataTypeBoolean } from "../../../shared/utils/convertDataTypeBoolean";
import {
  AttributeDataType,
  CustomField,
  CustomFieldResourceType,
  PayeeRow,
  PayeeRowAutoPayStrategy,
  PayeeRowTaxInformationShare,
  SearchPayeeRowsFilterParameter
} from "@wingspanhq/wingspan-api-sdk";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";

export type ContractorDataItem = {
  payee: PayeeRow;
  additionalData: CustomField[];
};

const mapCollaboratorToItemData = (item: ContractorDataItem) => {
  const { payee, additionalData } = item;

  if (!payee) {
    return {};
  }

  const profile = payee.profile;
  const customFields = payee.customFields || {};

  return {
    Name: selectorSearchRowName(payee),
    "First Name": profile?.firstName,
    "Last Name": profile?.lastName,
    "Company Name": profile?.companyName,
    // "Legal Business Name" is dropped: v3 `SearchRowProfile` carries
    // `companyName` and `doingBusinessAs` (the DBA) but no legal business name.
    "External Contractor ID": payee.externalId,
    Email: payee.email,
    Location: [
      payee.location?.country,
      payee.location?.state,
      payee.location?.city
    ]
      .filter(Boolean)
      .join(", "),
    Status: payee.engagementStatus,
    Autopay:
      payee.autoPayStrategy === PayeeRowAutoPayStrategy.ALL ? "Yes" : "No",
    "Created At": payee.events?.createdAt,
    "Updated At": payee.events?.updatedAt,
    "Tax Info":
      payee.taxInformationShare === PayeeRowTaxInformationShare.ALLOW
        ? "Yes"
        : "No",
    "Contractor URL": `${window.location.host}/member/payees/${payee.id}`,
    ...(!additionalData || isEmpty(additionalData)
      ? {}
      : additionalData.reduce((acc, data) => {
          return {
            ...acc,
            [`Custom Field ${data.name}`]:
              data.dataType === AttributeDataType.BOOLEAN
                ? convertDataTypeBoolean(
                    customFields[data.key ?? ""],
                    "Yes",
                    "No"
                  )
                : customFields[data.key ?? ""] || "-"
          };
        }, {})),
    "Contractor ID": payee.id
  };
};

export const mapCollaboratorsToCSV = (data: Array<ContractorDataItem>) => {
  let cleanResult = [];

  const dirtyResult = data
    .map(d => {
      try {
        return mapCollaboratorToItemData(d);
      } catch (error) {
        console.error(error);
      }
      return {};
    })
    .filter(d => !isEmpty(d));

  cleanResult = dirtyResult.map(d =>
    cleanCSVData(d, dirtyResult)
  ) as Array<any>;

  return cleanResult;
};

/**
 * Contractors CSV export, on v3 `searchPayeeRows`.
 *
 * Was v1 `getAllEntries(getPayeeRows)`, whose search service throws
 * `NotMigrated`. The cursor is walked here rather than by a hook because this
 * runs imperatively on click; `fetchQueryData` is cache-first, so a page the
 * list already loaded is reused.
 *
 * Unchanged from v1: it exports every contractor and does not honor the active
 * tab or filters — the caller passes only a file name. The v3 filter is
 * accepted below so that can be fixed without touching this signature again.
 */
export const useMutationDownloadPayeesCSV = (
  config?: WSMutationConfig<any, WSServiceError>
) => {
  const xWingspanAccount = useXWingspanExpansion();
  const queryCustomFields = queriesCustomFields.listCustomFields.useQueryAll({
    filter: { resourceType: { eq: CustomFieldResourceType.PAYEE } }
  });
  const additionalData = queryCustomFields.flatData;

  return useWSMutation(
    async (params: {
      fileName: string;
      filter?: SearchPayeeRowsFilterParameter;
    }) => {
      const { fileName, filter } = params || {};

      const payees: PayeeRow[] = [];
      let token: string | undefined;

      do {
        const page = await queriesPayeeRows.searchPayeeRows.fetchQueryData({
          filter,
          page: token ? { token } : undefined,
          xWingspanAccount
        });
        payees.push(...(page.data ?? []));
        token = page.pagination?.nextPageToken || undefined;
      } while (token);

      const data: ContractorDataItem[] = payees.map(payee => ({
        payee,
        additionalData
      }));

      const csvData = mapCollaboratorsToCSV(data);

      await downloadCSV(csvData, `${fileName}(${toWSDateString(new Date())})`);
    },
    {
      ...config
    }
  );
};
